import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Check } from 'lucide-react';
import { FairVendorProduct } from '../../types';
import { vendorApi } from '../../services/api';
import { extractApiError } from '../../utils/errorUtils';
import { formatCurrency } from '../../utils/currency';
import ErrorAlert from '../ErrorAlert';

interface EditFairProductModalProps {
  fairProduct: FairVendorProduct;
  vendorId: number;
  fairId: number;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Modal for editing fair-specific product details
 * Allows updating price, quantity, featured status, and pre-order availability
 */
export default function EditFairProductModal({ fairProduct, vendorId, fairId, onClose, onSuccess }: EditFairProductModalProps) {
  const [fairPrice, setFairPrice] = useState(fairProduct.fair_specific_price || fairProduct.product?.price || '');
  const [quantity, setQuantity] = useState(fairProduct.quantity_available ?? 0);
  const [isFeatured, setIsFeatured] = useState(fairProduct.is_featured);
  const [allowPreorder, setAllowPreorder] = useState(fairProduct.is_available_for_preorder);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Auto-focus first input on mount
  useEffect(() => {
    const firstInput = dialogRef.current?.querySelector('input');
    firstInput?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await vendorApi.updateFairProductDetails(vendorId, fairId, fairProduct.product_id, {
        fair_specific_price: fairPrice !== fairProduct.product?.price ? fairPrice : undefined,
        quantity_available: quantity,
        is_featured: isFeatured,
        is_available_for_preorder: allowPreorder
      });
      onSuccess();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to update fair product:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="presentation"
      tabIndex={-1}
      onClick={() => !loading && onClose()}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !loading) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-fair-product-modal-title"
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="edit-fair-product-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
          Edit Fair Details: {fairProduct.product?.name}
        </h3>

        {error && (
          <ErrorAlert
            message={error}
            onDismiss={() => setError(null)}
            dismissible={!loading}
            className="mb-4"
          />
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fair Price
            </label>
            <input
              type="text"
              value={fairPrice}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty, digits, and single decimal point with up to 2 decimal places
                if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                  setFairPrice(value);
                }
              }}
              placeholder={`Base price: ${formatCurrency(fairProduct.product?.price || '0')}`}
              pattern="^\d+(\.\d{0,2})?$"
              inputMode="decimal"
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity Available <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              max="100000"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              required
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="featured-edit"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              disabled={loading}
              className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <label htmlFor="featured-edit" className="text-sm text-gray-700">
              Mark as featured product
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="preorder-edit"
              checked={allowPreorder}
              onChange={(e) => setAllowPreorder(e.target.checked)}
              disabled={loading}
              className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <label htmlFor="preorder-edit" className="text-sm text-gray-700">
              Allow pre-orders
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
